import { useEffect, useState } from 'react';
import { authFetch } from '@/utils/auth-fetch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { CARD_RENDERERS } from './RuleImpactSection';

interface ProductDetail {
  pageGenerationId: string;
  sourceId: string;
  productName: string;
  status: string;
  telemetry: Array<{ traceType: string; callSite: string | null; payload: Record<string, number> }>;
}

export function ProductDetailPanel({ runId, pageGenerationId, onClose }: { runId: string; pageGenerationId: string; onClose: () => void }) {
  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch(`/feed-imports/runs/${runId}/report/products/${pageGenerationId}`)
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || 'Request failed');
        }
        return res.json();
      })
      .then((json: ProductDetail) => { if (!cancelled) setDetail(json); })
      .catch((err) => { if (!cancelled) setError(err.message || 'Could not load product detail'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [runId, pageGenerationId]);

  return (
    <Sheet open onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{detail?.productName || 'Product detail'}</SheetTitle>
          <SheetDescription className="sr-only">Rule-impact telemetry detail for this product.</SheetDescription>
        </SheetHeader>
        {loading && <Skeleton className="h-32 w-full mt-4" />}
        {!loading && error && (
          <p className="text-destructive text-sm mt-4">Could not load product detail: {error}</p>
        )}
        {!loading && !error && detail && detail.telemetry.length === 0 && (
          <p className="text-muted-foreground text-sm mt-4">No rule-impact data for this product.</p>
        )}
        {!loading && !error && detail && detail.telemetry.length > 0 && (
          <div className="mt-4 space-y-3">
            {detail.telemetry.map((t, i) => {
              const renderer = CARD_RENDERERS[t.traceType];
              if (!renderer) return null;
              return (
                <div key={`${t.traceType}-${i}`} className="text-sm">
                  <div className="font-semibold">{renderer.title}</div>
                  <div className="text-muted-foreground">{renderer.render(t.payload, 1)}</div>
                </div>
              );
            })}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
